// App.jsx — routeur UNIQUEMENT. Moins de 80 lignes, c'est la règle.
// Chez AGC, `app.jsx` faisait 631 lignes, portait 18 états et n'avait
// pas de routeur du tout : aucune URL n'était partageable.

import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useSession } from './lib/session.jsx';
import Shell from './ui/Shell.jsx';
import { SkeletonRows } from './ui/States.jsx';
import LoginPage from './features/auth/LoginPage.jsx';
import DevisListPage from './features/devis/DevisListPage.jsx';
import DevisPage from './features/devis/DevisPage.jsx';
import ClientsPage from './features/clients/ClientsPage.jsx';
import ParametresPage from './features/parametres/ParametresPage.jsx';
import ComptePage from './features/compte/ComptePage.jsx';
import ApprentissagePage from './features/apprentissage/ApprentissagePage.jsx';
import UtilisateursPage from './features/admin/UtilisateursPage.jsx';
import EtatPage from './features/admin/EtatPage.jsx';
import RetourAtelierPage from './features/atelier/RetourAtelierPage.jsx';
import CourrierPage from './features/courrier/CourrierPage.jsx';
import FeuilleAtelier from './features/atelier/FeuilleAtelier.jsx';
import AssistantPage from './features/assistant/AssistantPage.jsx';
import AllerVers from './features/assistant/AllerVers.jsx';

const NAV = [
  { to: '/devis', label: 'Devis' },
  // Le courrier vient juste après les devis : c'est de là que les
  // devis naissent désormais, et c'est le premier écran qu'on ouvre le
  // matin.
  { to: '/courrier', label: 'Courrier' },
  { to: '/clients', label: 'Clients' },
  { to: '/apprentissage', label: 'Apprentissage' },
  { to: '/assistant', label: 'Assistant' },
  { to: '/retour-atelier', label: 'Retour atelier' },
  { to: '/parametres', label: 'Paramètres' },
  { to: '/compte', label: 'Mon compte' },
  { to: '/administration/utilisateurs', label: 'Utilisateurs', admin: true },
  { to: '/administration/etat', label: 'État du service', admin: true },
];

// Le titre vient de NAV : deux listes à tenir en phase, c'en est une
// qui finit par mentir.
const TITLES = Object.fromEntries(NAV.map((n) => [n.to, n.label]));

function RequireAdmin({ children }) {
  const { isAdmin } = useSession();
  return isAdmin ? children : <Navigate to="/devis" replace />;
}

export function App() {
  const { user, checking } = useSession();
  const { pathname } = useLocation();

  // ══════════════════════════════════════════════════════════
  // LA FEUILLE D'ATELIER EST HORS DE TOUT LE RESTE.
  // ══════════════════════════════════════════════════════════
  //
  // Pas de session, pas de coquille, pas de barre latérale. Un opérateur
  // devant sa machine scanne un QR : s'il tombe sur un écran de
  // connexion, il repart — et le retour d'atelier n'existe pas.
  //
  // Le test est AVANT la vérification du cookie, et pas seulement avant
  // la redirection : sinon la page attendrait un `/auth/me` qui ne la
  // concerne pas, et clignoterait sur un téléphone en 3G.
  //
  // Ce que ce chemin peut atteindre est borné côté serveur : le jeton
  // ouvre UNE pièce, en écriture sur les seuls temps réels, et la
  // réponse ne porte ni prix, ni client, ni coordonnées.
  if (pathname.startsWith('/atelier/')) {
    return (
      <Routes>
        <Route path="/atelier/:token" element={<FeuilleAtelier />} />
      </Routes>
    );
  }

  // Tant que le cookie n'est pas vérifié, on n'affiche NI l'application
  // NI la connexion : sinon l'écran de connexion clignote à chaque
  // rechargement alors que la session est valide.
  if (checking) return <SkeletonRows rows={3} />;
  if (!user) return <LoginPage />;

  const nav = NAV.filter((item) => !item.admin || user.role === 'admin');
  const title = TITLES[pathname] ?? (pathname.startsWith('/devis/') ? 'Devis' : 'Mertens Devis');

  return (
    <Shell nav={nav} title={title}>
      <Routes>
        <Route path="/" element={<Navigate to="/devis" replace />} />
        <Route path="/devis" element={<DevisListPage />} />
        <Route path="/devis/:id" element={<DevisPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/apprentissage" element={<ApprentissagePage />} />
        <Route path="/retour-atelier" element={<RetourAtelierPage />} />
        <Route path="/courrier" element={<CourrierPage />} />
        <Route path="/assistant" element={<AssistantPage />} />
        <Route path="/aller/devis/:cible" element={<AllerVers type="devis" />} />
        <Route path="/aller/piece/:cible" element={<AllerVers type="piece" />} />
        <Route path="/parametres" element={<ParametresPage />} />
        <Route path="/compte" element={<ComptePage />} />
        <Route
          path="/administration/utilisateurs"
          element={<RequireAdmin><UtilisateursPage /></RequireAdmin>}
        />
        <Route
          path="/administration/etat"
          element={<RequireAdmin><EtatPage /></RequireAdmin>}
        />
        <Route path="*" element={<Navigate to="/devis" replace />} />
      </Routes>
    </Shell>
  );
}

export default App;
